import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  CardHeader,
  Collapse
} from 'reactstrap';
import Select from 'react-select';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import ResizableImage from './ResizableImage';
import { draftRawToPlainText, renderDraftContent } from '../../util/draftToHtml';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import {
  questionTextToolbar,
  choiceToolbar,
  explanationToolbar,
  lessonReferenceToolbar,
  editorContainerStyles
} from '../../util/editorToolbarConfig';

// Dynamic choice support constants
const MAX_CHOICES = 10;
const CHOICE_LABELS = Array.from({ length: MAX_CHOICES }, (_, i) => String.fromCharCode(65 + i));
const CORRECT_ANSWER_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [label, i.toString()]));

const BulkUploadReviewModal = ({
  isOpen,
  toggle,
  questions,
  subjects,
  units,
  topics,
  parseErrors,
  onSave,
  onUpdateQuestion,
  mode = 'create' // 'create' or 'edit'
}) => {
  const [selectAll, setSelectAll] = useState(true);
  const [selectedQuestions, setSelectedQuestions] = useState(
    questions.reduce((acc, _, i) => ({ ...acc, [i]: true }), {})
  );
  const [editingIndex, setEditingIndex] = useState(null);

  // Tags state for the currently editing question
  const [tagInput, setTagInput] = useState('');

  // Lazy WYSIWYG editor states — only created when a question is expanded
  const [editorStates, setEditorStates] = useState({});

  // Track questions length to avoid resetting on every question update
  const [questionsLength, setQuestionsLength] = useState(0);

  // Reset selected questions only when modal opens with NEW questions (different length)
  useEffect(() => {
    // Only reset if it's a completely new set of questions (different count)
    if (questions.length !== questionsLength) {
      setSelectedQuestions(questions.reduce((acc, _, i) => ({ ...acc, [i]: true }), {}));
      setSelectAll(true);
      setEditingIndex(null);
      setQuestionsLength(questions.length);
      setEditorStates({});
    }
  }, [questions.length, questionsLength]);

  // Helper: create EditorState from a value (plain text string or Draft.js raw object)
  const createEditorState = (value) => {
    if (!value) return EditorState.createEmpty();
    if (typeof value === 'object' && value.blocks) {
      try { return EditorState.createWithContent(convertFromRaw(value)); } catch (e) { console.warn('Failed to convert Draft.js content:', e); }
    }
    if (typeof value === 'string') return EditorState.createWithContent(ContentState.createFromText(value));
    return EditorState.createEmpty();
  };

  // Helper: get display text from a value (plain text string or Draft.js raw object)
  const getDisplayText = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value.blocks) return draftRawToPlainText(value);
    return '';
  };

  // Lazily initialize editor states when a question is expanded for editing
  useEffect(() => {
    if (editingIndex !== null && !editorStates[editingIndex]) {
      const question = questions[editingIndex];
      const choiceEditors = {};
      const numChoices = Math.max(question.choices?.length || 0, 2); // At least 2 choices
      for (let i = 0; i < numChoices; i++) {
        choiceEditors[`choice${CHOICE_LABELS[i]}`] = createEditorState(question.choices?.[i]?.text);
      }
      setEditorStates(prev => ({
        ...prev,
        [editingIndex]: {
          questionText: createEditorState(question.questionText),
          ...choiceEditors,
          explanation: createEditorState(question.explanation),
          lessonReference: createEditorState(question.lessonReference),
        }
      }));
    }
  }, [editingIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get filtered units based on selected subject
  const getFilteredUnits = (subjectId) => {
    if (!subjectId) return units || [];
    return (units || []).filter(u => u.subjectId === subjectId);
  };

  // Get filtered topics based on selected unit
  const getFilteredTopics = (unitId) => {
    if (!unitId) return topics || [];
    return (topics || []).filter(t => t.unitId === unitId);
  };

  // Handle select all
  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    setSelectedQuestions(questions.reduce((acc, _, i) => ({ ...acc, [i]: newValue }), {}));
  };

  // Handle individual selection
  const handleSelect = (index) => {
    const newSelected = { ...selectedQuestions, [index]: !selectedQuestions[index] };
    setSelectedQuestions(newSelected);
    setSelectAll(Object.values(newSelected).every(v => v));
  };

  // Toggle edit mode for a question
  const toggleEdit = (index) => {
    if (editingIndex === index) {
      setEditingIndex(null);
    } else {
      setEditingIndex(index);
    }
  };

  // Update question field (for dropdowns)
  const handleFieldChange = (index, field, value) => {
    const question = questions[index];
    const updated = { ...question };

    if (field === 'subjectId') {
      updated.subjectId = value;
      const subject = subjects.find(s => s.subjectId === value);
      updated.subjectName = subject?.name || '';
      // Reset unit and topic when subject changes
      updated.unitId = null;
      updated.unitName = '';
      updated.topicId = null;
      updated.topicName = '';
    } else if (field === 'unitId') {
      updated.unitId = value;
      const unit = (units || []).find(u => u.unitId === value);
      updated.unitName = unit?.name || '';
      // Reset topic when unit changes
      updated.topicId = null;
      updated.topicName = '';
    } else if (field === 'topicId') {
      updated.topicId = value;
      const topic = (topics || []).find(t => t.topicId === value);
      updated.topicName = topic?.name || '';
    } else if (field === 'correctAnswerLabel') {
      updated.correctAnswerLabel = value;
      updated.correctAnswer = [CORRECT_ANSWER_MAP[value]];
    }

    validateAndUpdateQuestion(index, updated);
  };

  // Check if a value has content (handles plain text strings, Draft.js raw objects, and image-only content)
  const hasContent = (value) => {
    if (!value) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'object' && value.blocks) {
      return value.blocks.some(b => b.text.trim().length > 0) ||
             Object.keys(value.entityMap || {}).length > 0;
    }
    return false;
  };

  // Validate and update question
  const validateAndUpdateQuestion = (index, updated) => {
    const errors = [];
    if (!updated.subjectId) errors.push('Subject is required');
    if (!updated.unitId) errors.push('Unit is required');
    if (!hasContent(updated.questionText)) errors.push('Question Text is required');
    if (!hasContent(updated.choices?.[0]?.text)) errors.push('Choice A is required');
    if (!hasContent(updated.choices?.[1]?.text)) errors.push('Choice B is required');
    if (!CHOICE_LABELS.includes(updated.correctAnswerLabel)) {
      errors.push(`Correct Answer must be one of: ${CHOICE_LABELS.join(', ')}`);
    }
    updated.hasValidationErrors = errors.length > 0;
    updated.validationErrors = errors;

    onUpdateQuestion(index, updated);
  };

  // Handle WYSIWYG editor changes — uses functional updater to avoid stale closure issues
  const handleEditorChange = (index, field, editorState) => {
    // Update local editor state
    setEditorStates(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: editorState }
    }));

    // Convert to raw and send field-specific updater to parent
    const raw = convertToRaw(editorState.getCurrentContent());

    onUpdateQuestion(index, (prevQuestion) => {
      const updated = { ...prevQuestion };

      if (field === 'questionText') {
        updated.questionText = raw;
      } else if (field === 'explanation') {
        updated.explanation = raw;
      } else if (field === 'lessonReference') {
        updated.lessonReference = raw;
      } else if (field.startsWith('choice')) {
        const choiceLetter = field.replace('choice', '');
        const choiceIdx = CHOICE_LABELS.indexOf(choiceLetter);
        if (choiceIdx >= 0) {
          const newChoices = [...(updated.choices || [])];
          while (newChoices.length <= choiceIdx) {
            newChoices.push({ choiceId: newChoices.length.toString(), text: '', label: CHOICE_LABELS[newChoices.length] });
          }
          newChoices[choiceIdx] = {
            ...newChoices[choiceIdx],
            choiceId: choiceIdx.toString(),
            text: raw,
            label: CHOICE_LABELS[choiceIdx]
          };
          updated.choices = newChoices;
        }
      }

      // Validate
      const errors = [];
      if (!updated.subjectId) errors.push('Subject is required');
      if (!updated.unitId) errors.push('Unit is required');
      if (!hasContent(updated.questionText)) errors.push('Question Text is required');
      if (!hasContent(updated.choices?.[0]?.text)) errors.push('Choice A is required');
      if (!hasContent(updated.choices?.[1]?.text)) errors.push('Choice B is required');
      if (!CHOICE_LABELS.includes(updated.correctAnswerLabel)) {
        errors.push(`Correct Answer must be one of: ${CHOICE_LABELS.join(', ')}`);
      }
      updated.hasValidationErrors = errors.length > 0;
      updated.validationErrors = errors;

      return updated;
    });
  };

  // Refs for stable image resize handler (avoids stale closures)
  const editorStatesRef = useRef(editorStates);
  editorStatesRef.current = editorStates;
  const handleEditorChangeRef = useRef(handleEditorChange);
  handleEditorChangeRef.current = handleEditorChange;

  // Block renderer for ResizableImage support in all editors
  const createBlockRenderer = useCallback((questionIndex, field) => (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: {
          onResize: (entityKey, newWidth) => {
            const currentEditorState = editorStatesRef.current[questionIndex]?.[field];
            if (!currentEditorState) return;
            const contentState = currentEditorState.getCurrentContent();
            const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
            const newEditorState = EditorState.push(currentEditorState, newContentState, 'apply-entity');
            handleEditorChangeRef.current(questionIndex, field, newEditorState);
          }
        }
      };
    }
    return undefined;
  }, []);

  // Render content as HTML (for Draft.js raw objects) or plain text (for strings)
  const renderContent = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
    if (typeof value === 'object' && value.blocks) return renderDraftContent(value);
    return null;
  };

  // Handle correct answer toggle
  const handleCorrectAnswerToggle = (choiceIndex) => {
    if (editingIndex === null) return;
    const question = questions[editingIndex];
    const updated = { ...question };
    updated.correctAnswerLabel = CHOICE_LABELS[choiceIndex];
    updated.correctAnswer = [CORRECT_ANSWER_MAP[updated.correctAnswerLabel]];
    onUpdateQuestion(editingIndex, updated);
  };

  // Handle adding tags
  const handleAddTag = () => {
    if (editingIndex === null || !tagInput.trim()) return;

    const question = questions[editingIndex];
    const updated = { ...question };

    // Split by comma and filter out empty strings and duplicates
    const currentTags = updated.tags || [];
    const newTags = tagInput
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag && !currentTags.includes(tag));

    if (newTags.length > 0) {
      updated.tags = [...currentTags, ...newTags];
      onUpdateQuestion(editingIndex, updated);
    }
    setTagInput('');
  };

  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    if (editingIndex === null) return;

    const question = questions[editingIndex];
    const updated = { ...question };
    updated.tags = (updated.tags || []).filter(t => t !== tagToRemove);
    onUpdateQuestion(editingIndex, updated);
  };

  // Get choice label from index
  const getChoiceLabel = (index) => {
    return String.fromCharCode(65 + index);
  };

  // Count valid questions
  const validCount = questions.filter(q => !q.hasValidationErrors && q.subjectId && q.unitId).length;
  const selectedCount = Object.values(selectedQuestions).filter(v => v).length;

  // Count update vs new (for edit mode)
  const updateCount = questions.filter(q => q.existingQuestionId && !q.hasValidationErrors && q.subjectId && q.unitId).length;
  const newCount = questions.filter(q => !q.existingQuestionId && !q.hasValidationErrors && q.subjectId && q.unitId).length;

  // Get questions to save (selected and valid)
  const questionsToSave = questions.filter((q, i) =>
    selectedQuestions[i] && !q.hasValidationErrors && q.subjectId && q.unitId
  );

  // Handle save
  const handleSave = () => {
    onSave(questionsToSave);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" keyboard={false} style={{ maxWidth: '95%' }}>
      <ModalHeader toggle={toggle}>
        <i className={`fas fa-${mode === 'edit' ? 'edit' : 'search'} mr-2 text-primary`}></i>
        {mode === 'edit' ? 'Review Questions Before Update' : 'Review Questions Before Upload'}
        <Badge color="info" className="ml-3">{questions.length} questions</Badge>
        {mode === 'edit' && (
          <>
            <Badge color="primary" className="ml-2">{updateCount} to update</Badge>
            {newCount > 0 && <Badge color="success" className="ml-2">{newCount} new</Badge>}
          </>
        )}
      </ModalHeader>
      <ModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        <style>{editorContainerStyles}</style>
        {/* Summary Stats */}
        <Row className="mb-3">
          <Col md="3">
            <Alert color="info" className="mb-0 py-2">
              <i className="fas fa-list mr-2"></i>
              Total: <strong>{questions.length}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="success" className="mb-0 py-2">
              <i className="fas fa-check mr-2"></i>
              Valid: <strong>{validCount}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="warning" className="mb-0 py-2">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Errors: <strong>{questions.length - validCount}</strong>
            </Alert>
          </Col>
          <Col md="3">
            <Alert color="primary" className="mb-0 py-2">
              <i className="fas fa-check-square mr-2"></i>
              Selected: <strong>{selectedCount}</strong>
            </Alert>
          </Col>
        </Row>

        {/* Parse errors */}
        {parseErrors.length > 0 && (
          <Alert color="danger" className="mb-3">
            <h5 className="alert-heading">
              <i className="fas fa-exclamation-circle mr-2"></i>
              Parse Errors:
            </h5>
            <ul className="mb-0">
              {parseErrors.map((err, i) => (
                <li key={i}>
                  {typeof err === 'string' ? err : `Row ${err.row}: ${err.errors.join(', ')}`}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Select All / Header Actions */}
        <Card className="mb-3 bg-lighter">
          <CardBody className="py-2">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="mr-2"
                  style={{ marginLeft: 0, position: 'relative' }}
                />
                <span className="font-weight-bold">
                  {selectAll ? 'Deselect All' : 'Select All'}
                </span>
              </div>
              <div className="text-muted">
                <i className="fas fa-info-circle mr-1"></i>
                Click <i className="fas fa-edit"></i> to edit a question with rich text editor
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Questions Cards */}
        {questions.map((question, index) => {
          const isSelected = selectedQuestions[index] || false;
          const isEditing = editingIndex === index;
          const filteredUnits = getFilteredUnits(question.subjectId);
          const filteredTopics = getFilteredTopics(question.unitId);
          const hasErrors = question.hasValidationErrors;
          const isValid = !hasErrors && question.subjectId && question.unitId;

          return (
            <Card
              key={index}
              className={`mb-3 ${hasErrors ? 'border-danger' : isValid ? 'border-success' : 'border-warning'}`}
              style={{ borderWidth: '2px' }}
            >
              <CardHeader className={`py-2 ${hasErrors ? 'bg-danger text-white' : isValid ? 'bg-success text-white' : 'bg-warning'}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <Input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(index)}
                      className="mr-3"
                      style={{ marginLeft: 0, position: 'relative' }}
                    />
                    <Badge color="light" className="mr-2 text-dark">
                      Row {question.rowNumber}
                    </Badge>
                    {mode === 'edit' && (
                      question.existingQuestionId ? (
                        <Badge color="primary" className="mr-2">
                          <i className="fas fa-sync-alt mr-1"></i>Update
                        </Badge>
                      ) : (
                        <Badge color="success" className="mr-2">
                          <i className="fas fa-plus mr-1"></i>New
                        </Badge>
                      )
                    )}
                    {mode === 'edit' && question.existingQuestionCode && (
                      <Badge color="light" className="mr-2 text-dark">
                        {question.existingQuestionCode}
                      </Badge>
                    )}
                    {question.subjectName && (
                      <Badge color="primary" className="mr-2">{question.subjectName}</Badge>
                    )}
                    {question.unitName && (
                      <Badge color="info" className="mr-2">{question.unitName}</Badge>
                    )}
                    {question.topicName && (
                      <Badge color="light" className="mr-2 text-dark">{question.topicName}</Badge>
                    )}
                    {question.passageTitle && (
                      <Badge color="success" className="mr-2">
                        <i className="fas fa-file-alt mr-1"></i>{question.passageTitle}
                      </Badge>
                    )}
                  </div>
                  <div>
                    {hasErrors && (
                      <Badge color="light" className="mr-2 text-danger">
                        <i className="fas fa-times mr-1"></i>
                        {question.validationErrors.length} Error{question.validationErrors.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                    <Button
                      color={isEditing ? 'light' : 'warning'}
                      size="sm"
                      onClick={() => toggleEdit(index)}
                      className="ml-2"
                    >
                      <i className={`fas fa-${isEditing ? 'times' : 'edit'} mr-1`}></i>
                      {isEditing ? 'Close' : 'Edit'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody>
                {/* Validation Errors */}
                {hasErrors && !isEditing && (
                  <Alert color="danger" className="mb-3 py-2">
                    <strong>Errors:</strong> {question.validationErrors.join(', ')}
                  </Alert>
                )}

                {/* Edit Mode - Full Rich Text Editors */}
                <Collapse isOpen={isEditing}>
                  <Card className="mb-3 bg-lighter">
                    <CardBody>
                      {/* Subject, Unit, Topic Selection */}
                      <Row>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Subject <span className="text-danger">*</span>
                            </Label>
                            <Select
                              options={subjects.map(subject => ({
                                value: subject.subjectId,
                                label: subject.code ? `${subject.code} ${subject.name}` : subject.name
                              }))}
                              value={
                                question.subjectId
                                  ? {
                                      value: question.subjectId,
                                      label: (() => {
                                        const s = subjects.find(s => s.subjectId === question.subjectId);
                                        return s?.code ? `${s.code} ${s.name}` : s?.name;
                                      })()
                                    }
                                  : null
                              }
                              onChange={(option) => handleFieldChange(index, 'subjectId', option?.value || '')}
                              styles={customDropdownWithBorder}
                              placeholder="Select a subject"
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">
                              Unit <span className="text-danger">*</span>
                            </Label>
                            <Select
                              options={filteredUnits.map(unit => ({
                                value: unit.unitId,
                                label: unit.code ? `${unit.code} ${unit.name}` : unit.name
                              }))}
                              value={
                                question.unitId
                                  ? {
                                      value: question.unitId,
                                      label: (() => {
                                        const u = filteredUnits.find(u => u.unitId === question.unitId);
                                        return u?.code ? `${u.code} ${u.name}` : u?.name;
                                      })()
                                    }
                                  : null
                              }
                              onChange={(option) => handleFieldChange(index, 'unitId', option?.value || '')}
                              styles={customDropdownWithBorder}
                              placeholder="Select a unit"
                              isDisabled={!question.subjectId}
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                        <Col md="4">
                          <FormGroup>
                            <Label className="form-control-label">Topic (Optional)</Label>
                            <Select
                              options={filteredTopics.map(topic => ({
                                value: topic.topicId,
                                label: topic.code ? `${topic.code} ${topic.name}` : topic.name
                              }))}
                              value={
                                question.topicId
                                  ? {
                                      value: question.topicId,
                                      label: (() => {
                                        const t = filteredTopics.find(t => t.topicId === question.topicId);
                                        return t?.code ? `${t.code} ${t.name}` : t?.name;
                                      })()
                                    }
                                  : null
                              }
                              onChange={(option) => handleFieldChange(index, 'topicId', option?.value || '')}
                              styles={customDropdownWithBorder}
                              placeholder="Select a topic"
                              isDisabled={!question.unitId || filteredTopics.length === 0}
                              isClearable
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      {/* Question Text - WYSIWYG Editor */}
                      <FormGroup>
                        <Label className="form-control-label d-flex align-items-center">
                          Question Text <span className="text-danger ml-1">*</span>
                        </Label>
                        {editorStates[index] && (
                          <div className="border rounded p-2 question-editor-container">
                            <Editor
                              editorState={editorStates[index].questionText}
                              onEditorStateChange={(state) => handleEditorChange(index, 'questionText', state)}
                              toolbar={questionTextToolbar}
                              customBlockRenderFunc={createBlockRenderer(index, 'questionText')}
                            />
                          </div>
                        )}
                      </FormGroup>

                      {/* Answer Choices with Radio Buttons - WYSIWYG Editors */}
                      <FormGroup>
                        <Label className="form-control-label">
                          Answer Choices <span className="text-danger">*</span> (Select the correct answer)
                        </Label>
                        {Array.from({ length: Math.max(question.choices?.length || 0, 2) }, (_, choiceIdx) => {
                          const choiceField = `choice${getChoiceLabel(choiceIdx)}`;
                          return (
                            <div key={choiceIdx} className="mb-3">
                              <div className="d-flex align-items-start">
                                <div className="d-flex align-items-center mr-2" style={{ minWidth: '60px', paddingTop: '10px' }}>
                                  <div className="custom-control custom-radio">
                                    <input
                                      type="radio"
                                      className="custom-control-input"
                                      id={`bulk-choice-${index}-${choiceIdx}`}
                                      name={`bulk-correctAnswer-${index}`}
                                      checked={question.correctAnswerLabel === getChoiceLabel(choiceIdx)}
                                      onChange={() => handleCorrectAnswerToggle(choiceIdx)}
                                    />
                                    <label className="custom-control-label" htmlFor={`bulk-choice-${index}-${choiceIdx}`}></label>
                                  </div>
                                  <span className="ml-2 font-weight-bold text-muted">
                                    {getChoiceLabel(choiceIdx)}.
                                    {choiceIdx < 2 && <span className="text-danger">*</span>}
                                  </span>
                                </div>
                                <div className="flex-grow-1">
                                  {editorStates[index] && editorStates[index][choiceField] && (
                                    <div className="border rounded p-2 choice-editor-container">
                                      <Editor
                                        editorState={editorStates[index][choiceField]}
                                        onEditorStateChange={(state) => handleEditorChange(index, choiceField, state)}
                                        toolbar={choiceToolbar}
                                        customBlockRenderFunc={createBlockRenderer(index, choiceField)}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </FormGroup>

                      {/* Explanation - WYSIWYG Editor */}
                      <FormGroup>
                        <Label className="form-control-label d-flex align-items-center">
                          Explanation
                        </Label>
                        {editorStates[index] && (
                          <div className="border rounded p-2 explanation-editor-container">
                            <Editor
                              editorState={editorStates[index].explanation}
                              onEditorStateChange={(state) => handleEditorChange(index, 'explanation', state)}
                              toolbar={explanationToolbar}
                              customBlockRenderFunc={createBlockRenderer(index, 'explanation')}
                            />
                          </div>
                        )}
                      </FormGroup>

                      {/* Lesson Reference - WYSIWYG Editor */}
                      <FormGroup>
                        <Label className="form-control-label">Lesson Reference (Optional)</Label>
                        {editorStates[index] && (
                          <div className="border rounded p-2 lesson-editor-container">
                            <Editor
                              editorState={editorStates[index].lessonReference}
                              onEditorStateChange={(state) => handleEditorChange(index, 'lessonReference', state)}
                              toolbar={lessonReferenceToolbar}
                              customBlockRenderFunc={createBlockRenderer(index, 'lessonReference')}
                            />
                          </div>
                        )}
                      </FormGroup>

                      {/* Tags */}
                      <FormGroup>
                        <Label className="form-control-label">Tags (Optional)</Label>
                        <div className="d-flex mb-2">
                          <Input
                            type="text"
                            placeholder="Add tags (separate with commas)"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddTag();
                              }
                            }}
                          />
                          <Button color="primary" className="ml-2" onClick={handleAddTag} style={{ whiteSpace: 'nowrap' }}>
                            <i className="fas fa-plus mr-2"></i>
                            Add
                          </Button>
                        </div>
                        <div>
                          {(question.tags || []).map((tag, tagIndex) => (
                            <Badge key={tagIndex} color="info" className="mr-2 mb-2" style={{ fontSize: '14px' }}>
                              {tag}
                              <Button
                                close
                                size="sm"
                                className="ml-2"
                                onClick={() => handleRemoveTag(tag)}
                              />
                            </Badge>
                          ))}
                        </div>
                      </FormGroup>

                      {hasErrors && (
                        <Alert color="danger" className="mb-0">
                          <strong>Remaining Errors:</strong> {question.validationErrors.join(', ')}
                        </Alert>
                      )}
                    </CardBody>
                  </Card>
                </Collapse>

                {/* Changes Summary for Update mode */}
                {mode === 'edit' && question.existingQuestionId && question.changes && question.changes.length > 0 && !isEditing && (
                  <Alert color="info" className="mb-3 py-2">
                    <strong><i className="fas fa-exchange-alt mr-1"></i> Changes detected:</strong>
                    <ul className="mb-0 mt-1 pl-3">
                      {question.changes.map((change, idx) => (
                        <li key={idx}>
                          <strong>{change.field}:</strong>{' '}
                          <span className="text-muted">{change.oldValue || '(empty)'}</span>
                          {' → '}
                          <span className="text-success">{change.newValue || '(empty)'}</span>
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {/* Preview Mode (when not editing) */}
                {!isEditing && (
                  <>
                    <div className="mb-3">
                      <h5 className="mb-2 text-muted">
                        <i className="fas fa-question-circle mr-2"></i>
                        Question:
                      </h5>
                      <div className="mb-0">
                        {renderContent(question.questionText) || <span className="text-danger font-italic">Missing question text</span>}
                      </div>
                    </div>

                    <div className="mb-3">
                      <h5 className="mb-2 text-muted">
                        <i className="fas fa-list-ul mr-2"></i>
                        Choices:
                      </h5>
                      {question.choices?.map((choice, idx) => (
                        <div
                          key={idx}
                          className={`d-flex align-items-start mb-2 p-2 rounded ${
                            question.correctAnswerLabel === getChoiceLabel(idx)
                              ? 'bg-success text-white'
                              : 'bg-lighter'
                          }`}
                        >
                          <Badge
                            color={question.correctAnswerLabel === getChoiceLabel(idx) ? 'light' : 'light'}
                            className={`mr-2 ${question.correctAnswerLabel !== getChoiceLabel(idx) ? 'text-dark' : ''}`}
                            style={{ minWidth: '28px' }}
                          >
                            {question.correctAnswerLabel === getChoiceLabel(idx) ? '✓' : getChoiceLabel(idx)}
                          </Badge>
                          <div style={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }}>
                            {renderContent(choice.text) || <span className="text-danger font-italic">Empty</span>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {getDisplayText(question.explanation) && (
                      <div className="mb-3">
                        <h5 className="mb-2 text-muted">
                          <i className="fas fa-lightbulb mr-2"></i>
                          Explanation:
                        </h5>
                        <div className="mb-0 text-muted">
                          {renderContent(question.explanation)}
                        </div>
                      </div>
                    )}

                    {getDisplayText(question.lessonReference) && (
                      <div className="mb-3">
                        <h5 className="mb-2 text-muted">
                          <i className="fas fa-book mr-2"></i>
                          Lesson Reference:
                        </h5>
                        <div className="mb-0 text-muted">
                          {renderContent(question.lessonReference)}
                        </div>
                      </div>
                    )}

                    {question.tags && question.tags.length > 0 && (
                      <div>
                        <h5 className="mb-2 text-muted">
                          <i className="fas fa-tags mr-2"></i>
                          Tags:
                        </h5>
                        <div>
                          {question.tags.map((tag, tagIndex) => (
                            <Badge key={tagIndex} color="info" className="mr-2">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          );
        })}
      </ModalBody>
      <ModalFooter className="bg-lighter">
        <div className="d-flex w-100 justify-content-between align-items-center">
          <div>
            <span className="text-muted mr-3">
              <i className="fas fa-check-square mr-1"></i>
              {selectedCount} selected
            </span>
            <span className={questionsToSave.length > 0 ? 'text-success font-weight-bold' : 'text-muted'}>
              <i className={`fas fa-${mode === 'edit' ? 'save' : 'upload'} mr-1`}></i>
              {questionsToSave.length} ready to {mode === 'edit' ? 'save' : 'upload'}
            </span>
          </div>
          <div>
            <Button color="secondary" onClick={toggle} className="mr-2">
              <i className="fas fa-times mr-2"></i>
              Cancel
            </Button>
            <Button
              color="success"
              onClick={handleSave}
              disabled={questionsToSave.length === 0}
            >
              <i className={`fas fa-${mode === 'edit' ? 'save' : 'upload'} mr-2`}></i>
              {mode === 'edit' ? `Save ${questionsToSave.length} Questions` : `Upload ${questionsToSave.length} Questions`}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default BulkUploadReviewModal;
